import React, { useEffect, useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import MuiTextField from "@mui/material/TextField";
import MuiSelect from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Drawer from "components/common/Drawer";
import JarvisButton from "components/JarvisButton";

const EMPTY_FILTERS = {
  fromDate: null,
  toDate: null,
  transactionStatus: null,
};

const controlShellSx = {
  height: 40,
  minWidth: 0,
  borderRadius: "10px",
  border: "1px solid #E5E7EB",
  bgcolor: "#fff",
  px: 1.5,
  display: "flex",
  alignItems: "center",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  "&:hover": { borderColor: "#CBD5E1" },
  "&:focus-within": {
    borderColor: "#93C5FD",
    boxShadow: "0 0 0 3px rgba(147,197,253,0.35)",
  },
};

const dateInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 38,
    fontSize: 14,
    color: "#374151",
    fontFamily: "inherit",
    bgcolor: "transparent",
    pr: 0.5,
    "& fieldset": { border: "none" },
    "& input": {
      p: "8px 0",
      fontSize: 14,
      color: "#374151",
      "&::placeholder": { color: "#9CA3AF", opacity: 1 },
    },
  },
  "& .MuiInputAdornment-root": { ml: 0 },
  "& .MuiIconButton-root": {
    p: 0.5,
    color: "#9CA3AF",
    "&:hover": { bgcolor: "transparent", color: "#6B7280" },
  },
};

const IconBtn = ({ onClick, ariaLabel, disabled = false, children }) => (
  <Box
    component="button"
    type="button"
    aria-label={ariaLabel}
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    sx={{
      width: 40,
      height: 40,
      borderRadius: "10px",
      border: "1px solid #E5E7EB",
      bgcolor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "default" : "pointer",
      p: 0,
      flexShrink: 0,
      opacity: disabled ? 0.4 : 1,
      transition: "background-color 0.15s ease, border-color 0.15s ease",
      "&:hover": disabled
        ? {}
        : { bgcolor: "#F9FAFB", borderColor: "#CBD5E1" },
    }}
  >
    {children}
  </Box>
);

const formatDisplayDate = (value) => {
  if (!value) return "";
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch (e) {
    return "";
  }
};

const BillingDateField = ({ value, onChange, placeholder, width }) => (
  <Box sx={{ ...controlShellSx, width }}>
    <DesktopDatePicker
      disableFuture
      value={value}
      onChange={onChange}
      inputFormat="dd/MM/yyyy"
      components={{
        OpenPickerIcon: () => (
          <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
        ),
      }}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          fullWidth
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            placeholder,
          }}
          sx={dateInputSx}
        />
      )}
    />
  </Box>
);

const BillingStatusSelect = ({
  value,
  onChange,
  options = [],
  width,
  fullWidth = false,
}) => (
  <Box
    sx={{
      ...controlShellSx,
      width: fullWidth ? "100%" : width,
      pr: 1,
    }}
  >
    <FormControl fullWidth variant="standard">
      <MuiSelect
        value={value || ""}
        displayEmpty
        disableUnderline
        onChange={(e) => onChange(e.target.value || null)}
        IconComponent={KeyboardArrowDownRoundedIcon}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Text sx={{ fontSize: 14, color: "#9CA3AF" }}>
                Transaction Status
              </Text>
            );
          }
          const match = options.find((o) => o.value === selected);
          return (
            <Text sx={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>
              {match?.label || selected}
            </Text>
          );
        }}
        sx={{
          fontSize: 14,
          color: "#374151",
          "& .MuiSelect-select": {
            py: "8px",
            pr: "28px !important",
          },
          "& .MuiSelect-icon": {
            color: "#9CA3AF",
            right: 0,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: "10px",
              mt: 0.5,
              boxShadow: "0px 8px 24px rgba(15,23,42,0.12)",
            },
          },
        }}
      >
        <MenuItem value="">
          <Text sx={{ fontSize: 14, color: "#9CA3AF" }}>All statuses</Text>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Text sx={{ fontSize: 14, color: "#374151" }}>{opt.label}</Text>
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  </Box>
);

const DesktopFilters = ({
  filterData,
  options,
  onFilterChange,
  onClearFilters,
  isFilterApplied,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ flexShrink: 0 }}
  >
    <BillingDateField
      width={156}
      placeholder="From Date"
      value={filterData.fromDate}
      onChange={(value) => onFilterChange({ name: "fromDate", value })}
    />
    <BillingDateField
      width={156}
      placeholder="To Date"
      value={filterData.toDate}
      onChange={(value) => onFilterChange({ name: "toDate", value })}
    />
    <BillingStatusSelect
      width={180}
      value={filterData.transactionStatus}
      options={options}
      onChange={(value) =>
        onFilterChange({ name: "transactionStatus", value })
      }
    />
    <IconBtn
      ariaLabel="Clear filters"
      onClick={onClearFilters}
      disabled={!isFilterApplied}
    >
      <CloseRoundedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
    </IconBtn>
  </Stack>
);

const MobileFilters = ({
  filterData,
  options,
  onReplaceFilters,
  onClearFilters,
  isFilterApplied,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filterData);

  useEffect(() => {
    if (open) setDraft(filterData);
  }, [open, filterData]);

  const updateDraft = (name, value) => {
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    onReplaceFilters(draft);
    setOpen(false);
  };

  const handleReset = () => {
    setDraft({ ...EMPTY_FILTERS });
    onClearFilters();
    setOpen(false);
  };

  const activeSummary = [
    filterData.fromDate ? `From ${formatDisplayDate(filterData.fromDate)}` : null,
    filterData.toDate ? `To ${formatDisplayDate(filterData.toDate)}` : null,
    filterData.transactionStatus
      ? options.find((o) => o.value === filterData.transactionStatus)?.label
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          sx={{
            ...controlShellSx,
            height: 40,
            px: 1.75,
            gap: 1,
            cursor: "pointer",
            borderColor: isFilterApplied ? "#93C5FD" : "#E5E7EB",
            bgcolor: isFilterApplied ? "#EFF6FF" : "#fff",
          }}
        >
          <FilterListRoundedIcon sx={{ fontSize: 18, color: "#4B5563" }} />
          <Text sx={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
            Filters
          </Text>
          {isFilterApplied ? (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#2563EB",
              }}
            />
          ) : null}
        </Box>
        <IconBtn
          ariaLabel="Clear filters"
          onClick={onClearFilters}
          disabled={!isFilterApplied}
        >
          <CloseRoundedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
        </IconBtn>
      </Stack>

      {isFilterApplied && activeSummary ? (
        <Text
          sx={{
            mt: 1,
            fontSize: 12,
            color: "#6B7280",
            lineHeight: 1.4,
          }}
        >
          {activeSummary}
        </Text>
      ) : null}

      <Drawer
        open={open}
        anchor="bottom"
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            top: "auto !important",
            height: "auto !important",
            maxHeight: "85vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            paddingBottom: "var(--safe-area-inset-bottom)",
          },
        }}
      >
        <Box sx={{ px: 2.5, pt: 2.5, pb: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2.5 }}
          >
            <Text sx={{ fontWeight: 700, fontSize: 18, color: "#111827" }}>
              Filters
            </Text>
            <IconBtn ariaLabel="Close filters" onClick={() => setOpen(false)}>
              <CloseRoundedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
            </IconBtn>
          </Stack>

          <Stack spacing={2}>
            <Box>
              <Text sx={{ fontSize: 13, fontWeight: 500, color: "#6B7280", mb: 1 }}>
                From Date
              </Text>
              <BillingDateField
                width="100%"
                placeholder="Select date"
                value={draft.fromDate}
                onChange={(value) => updateDraft("fromDate", value)}
              />
            </Box>
            <Box>
              <Text sx={{ fontSize: 13, fontWeight: 500, color: "#6B7280", mb: 1 }}>
                To Date
              </Text>
              <BillingDateField
                width="100%"
                placeholder="Select date"
                value={draft.toDate}
                onChange={(value) => updateDraft("toDate", value)}
              />
            </Box>
            <Box>
              <Text sx={{ fontSize: 13, fontWeight: 500, color: "#6B7280", mb: 1 }}>
                Transaction Status
              </Text>
              <BillingStatusSelect
                fullWidth
                value={draft.transactionStatus}
                options={options}
                onChange={(value) => updateDraft("transactionStatus", value)}
              />
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
            <JarvisButton
              disableEndIcon
              disableStartIcon
              variant="outlined"
              onClick={handleReset}
              sx={{ flex: 1 }}
            >
              Reset
            </JarvisButton>
            <JarvisButton
              disableEndIcon
              disableStartIcon
              onClick={handleApply}
              sx={{ flex: 1 }}
            >
              Apply
            </JarvisButton>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

/**
 * Polished billing filters — inline on desktop, drawer on mobile.
 */
const BillingFilters = ({
  isMobile = false,
  filterData,
  transactionStatusOptions = [],
  onFilterChange,
  onReplaceFilters,
  onClearFilters,
  isFilterApplied = false,
}) => {
  if (isMobile) {
    return (
      <MobileFilters
        filterData={filterData}
        options={transactionStatusOptions}
        onReplaceFilters={onReplaceFilters}
        onClearFilters={onClearFilters}
        isFilterApplied={isFilterApplied}
      />
    );
  }

  return (
    <DesktopFilters
      filterData={filterData}
      options={transactionStatusOptions}
      onFilterChange={onFilterChange}
      onClearFilters={onClearFilters}
      isFilterApplied={isFilterApplied}
    />
  );
};

export default BillingFilters;
